import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "@fontsource-variable/manrope";
import "@fontsource/tajawal/700.css";
import "@fontsource/barlow-condensed/500.css";
import "@fontsource/barlow-condensed/600.css";
import "@fontsource/barlow-condensed/200.css";
import "@fontsource/barlow-condensed/400.css";
import "@fontsource/barlow-condensed/700.css";
import "@fontsource/barlow-condensed/800.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/600.css";
import "./index.css";
import { BrowserRouter } from "react-router";
import { MotionConfig } from "motion/react";
import type { ReactNode } from "react";
import { useAppearanceStore } from "@/stores/appearanceStore";
import SiteRoutes from "./routes.tsx";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { env } from "@/config/envvars.ts";
import { DefaultToastRegion } from "@/components/Toast.tsx";
import MetadataProvider from "@/providers/MetadataProvider.tsx";
import { backendRetryDelay, shouldRetryRequest } from "@/lib/backendHealth.ts";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Pages not yet on the realtime socket (lib/socket.ts) rely on polling +
      // focus refetch (see creative-os-backend/src/infrastructure/Services/Socket/README.md).
      // Coming back to the tab reconciles everything that moved server-side.
      refetchOnWindowFocus: true,
      // Global retry policy: only network errors / 5xx are retried, at most
      // MAX_BACKEND_RETRIES times with a capped backoff. Past that the outage
      // page takes over (see lib/backendHealth.ts). Per-query `retry` overrides
      // still apply where a hook sets one.
      retry: shouldRetryRequest,
      retryDelay: backendRetryDelay,
    },
    mutations: {
      // Mutations are never replayed automatically — the user re-submits.
      retry: false,
    },
  },
});

/** Settings → Appearance → Reduce motion forces what the OS setting does. */
function AppMotionConfig({ children }: { children: ReactNode }) {
  const reduceMotion = useAppearanceStore((s) => s.reduceMotion);
  return <MotionConfig reducedMotion={reduceMotion ? "always" : "user"}>{children}</MotionConfig>;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MetadataProvider>
      <GoogleOAuthProvider clientId={env.GOOGLE_OAUTH_CLIENT_ID}>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            {/* Library, Foundations / Motion: when the OS asks for reduced motion,
                transform and layout animation switch off; opacity and colour remain. */}
            <AppMotionConfig>
              <DefaultToastRegion />
              <SiteRoutes />
            </AppMotionConfig>
          </QueryClientProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </MetadataProvider>
  </StrictMode>,
);
